/**
 * Persisted shape of the editor onboarding mentor session.
 *
 * V1 storage is localStorage-only, keyed by user id. The shape is versioned
 * so a future migration to a server-backed store can read or discard old
 * values safely.
 */

export type EditorStepId =
  | "e0_welcome"
  | "e1_navbar"
  | "e2_hero"
  | "e3_accommodation_grid"
  | "e4_amenities_grid"
  | "e5_gallery_section"
  | "e6_host_story"
  | "e7_offer_cards"
  | "e8_destination_guide"
  | "e9_inquiry_band"
  | "e10_footer"
  | "e11_save"
  | "e12_history_button"
  | "e13_history_panel"
  | "e14_preview"
  | "e15_seo"
  | "e16_publish"
  | "e17_post_publish";

export const EDITOR_STEP_ORDER: EditorStepId[] = [
  "e0_welcome",
  "e1_navbar",
  "e2_hero",
  "e3_accommodation_grid",
  "e4_amenities_grid",
  "e5_gallery_section",
  "e6_host_story",
  "e7_offer_cards",
  "e8_destination_guide",
  "e9_inquiry_band",
  "e10_footer",
  "e11_save",
  "e12_history_button",
  "e13_history_panel",
  "e14_preview",
  "e15_seo",
  "e16_publish",
  "e17_post_publish",
];

export type SessionStatus =
  | "not_started"
  | "active"
  | "paused"
  | "skipped"
  | "dismissed"
  | "blocked"
  | "completed";

export type StepExitReason =
  | "completed"
  | "skipped"
  | "bypassed"
  | "failed"
  | "paused";

export interface StepHistoryEntry {
  stepId: EditorStepId;
  enteredAt: string;
  exitedAt: string | null;
  exitReason: StepExitReason | null;
}

export interface OnboardingSessionState {
  schemaVersion: 2;
  userId: string;
  sessionId: string;
  status: SessionStatus;
  currentStep: EditorStepId;
  branch: "blank" | "template" | "unknown";
  templateId: string | null;
  startedAt: string | null;
  lastActivityAt: string | null;
  completedAt: string | null;
  outcome: "published" | "finished_without_publish" | null;
  stepHistory: StepHistoryEntry[];
}

const STEP_HISTORY_CAP = 50;

export function createInitialSession(
  userId: string,
  sessionId: string,
): OnboardingSessionState {
  return {
    schemaVersion: 2,
    userId,
    sessionId,
    status: "not_started",
    currentStep: "e0_welcome",
    branch: "unknown",
    templateId: null,
    startedAt: null,
    lastActivityAt: null,
    completedAt: null,
    outcome: null,
    stepHistory: [],
  };
}

export function trimStepHistory(
  history: StepHistoryEntry[],
): StepHistoryEntry[] {
  if (history.length <= STEP_HISTORY_CAP) return history;
  return history.slice(history.length - STEP_HISTORY_CAP);
}

/**
 * Defensive read: returns null when the stored value does not match the
 * current schema. Future schema migrations branch here.
 */
export function parseStoredSession(
  raw: unknown,
): OnboardingSessionState | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Partial<OnboardingSessionState>;
  if (obj.schemaVersion !== 2) return null;
  if (typeof obj.userId !== "string" || obj.userId.length === 0) return null;
  if (typeof obj.sessionId !== "string" || obj.sessionId.length === 0) {
    return null;
  }
  if (typeof obj.status !== "string") return null;
  if (typeof obj.currentStep !== "string") return null;
  if (!Array.isArray(obj.stepHistory)) return null;
  // Best-effort. We never throw — bad shape just discards local state.
  return {
    schemaVersion: 2,
    userId: obj.userId,
    sessionId: obj.sessionId,
    status: obj.status as SessionStatus,
    currentStep: obj.currentStep as EditorStepId,
    branch: (obj.branch as OnboardingSessionState["branch"]) ?? "unknown",
    templateId: obj.templateId ?? null,
    startedAt: obj.startedAt ?? null,
    lastActivityAt: obj.lastActivityAt ?? null,
    completedAt: obj.completedAt ?? null,
    outcome: obj.outcome ?? null,
    stepHistory: trimStepHistory(obj.stepHistory as StepHistoryEntry[]),
  };
}
